import React, { useCallback, useEffect, useState } from "react";
import { Button, Dialog, Input, Table } from "components/ui";
import { HiOutlineSearch, HiX } from "react-icons/hi";
import { IoReloadCircleSharp } from "react-icons/io5";
import {
  completeSqrPaymentFlow,
  filterSqrTransactions,
  fetchSqrTransactions,
  getSqrPaymentCallbacks,
  markCashModalFromPaymentInitiated,
  processSqrBalancePayment,
  processSqrUpiTransaction,
} from "utilities/onlinePaymentFlow";
import OpenNotification from "utilities/ToastNotification";

const { Tr, Th, Td, THead, TBody } = Table;

const LIGHT_GREY_BTN_STYLE = {
  background: "#e5e7eb",
  color: "#374151",
  border: "1px solid #d1d5db",
};

const RESET_BTN_STYLE = {
  background: "#5783db",
  color: "#fff",
  minWidth: "5.75rem",
};

const RELOAD_BTN_STYLE = {
  background: "#55c2da",
  color: "#fff",
  minWidth: "5.75rem",
};

const getDefaultBillAmount = (detail) => {
  const amount = detail?.amountRs ?? detail?.paymentRequest?.amount;
  if (amount == null || amount === "") return "";
  return String(amount);
};

const formatTransactionDateTime = (transactionDate) => {
  if (!transactionDate) {
    return { date: "-", time: "" };
  }

  const str = String(transactionDate).trim();
  const parts = str.split(/\s+/);

  if (parts.length < 2) {
    return { date: str, time: "" };
  }

  const date = parts[0];
  const time = parts[1].split(".")[0];

  return { date, time };
};

const SqrPaymentTransactionsModal = ({ isOpen, detail, onClose, onDismiss }) => {
  const [transactions, setTransactions] = useState([]);
  const [displayedTransactions, setDisplayedTransactions] = useState([]);
  const [utrQuery, setUtrQuery] = useState("");
  const [amountRs, setAmountRs] = useState("");
  const [loading, setLoading] = useState(false);
  const [processingUtr, setProcessingUtr] = useState(null);
  const [searchMessage, setSearchMessage] = useState("");

  const pgId = detail?.pgId;

  useEffect(() => {
    if (!isOpen || !detail) return;
    const initial = Array.isArray(detail.transactions) ? detail.transactions : [];
    const billAmount = getDefaultBillAmount(detail);
    setTransactions(initial);
    setDisplayedTransactions(initial);
    setUtrQuery("");
    setAmountRs(billAmount);
    setSearchMessage("");
  }, [isOpen, detail]);

  const recordCount = displayedTransactions.length;

  const runFetch = useCallback(
    async ({ utr, amount, replaceList = true }) => {
      if (!pgId) return null;
      setLoading(true);
      setSearchMessage("");
      try {
        const result = await fetchSqrTransactions({
          pgId,
          sqr_utrtxnid: utr,
          amountRs: amount,
        });
        const nextTransactions = Array.isArray(result.transactions)
          ? result.transactions
          : [];

        if (replaceList) {
          setTransactions(nextTransactions);
          setDisplayedTransactions(nextTransactions);
        }

        if (result.isSqrPayment && normalizeStatus(result.status) !== "SUCCESS") {
          setSearchMessage("Transactions cannot be listed, try later.");
        }

        return result;
      } catch (error) {
        setSearchMessage("Unable to fetch transactions. Please try again.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [pgId]
  );

  const handleReload = () => {
    runFetch({
      utr: utrQuery.trim(),
      amount: amountRs.trim(),
      replaceList: true,
    });
  };

  const handleSearch = async () => {
    const filtered = filterSqrTransactions(transactions, utrQuery, amountRs);
    if (filtered.length > 0) {
      setDisplayedTransactions(filtered);
      setSearchMessage("");
      return;
    }

    const result = await runFetch({
      utr: utrQuery.trim(),
      amount: amountRs.trim(),
      replaceList: true,
    });

    if (result?.isSqrPayment && normalizeStatus(result.status) === "SUCCESS") {
      const next = filterSqrTransactions(
        result.transactions || [],
        utrQuery,
        amountRs
      );
      setDisplayedTransactions(next);
      if (next.length === 0 && (result.transactions || []).length > 0) {
        setDisplayedTransactions(result.transactions || []);
      }
    }
  };

  const handleReset = () => {
    setUtrQuery("");
    setAmountRs(getDefaultBillAmount(detail));
    setDisplayedTransactions(transactions);
    setSearchMessage("");
  };

  const handleProcess = async (txn) => {
    const billAmount = getDefaultBillAmount(detail);
    const utr = txn?.utr;
    const pgIdValue = detail?.pgId ?? detail?.paymentRequest?.gateway?.id;
    const pgBrandValue =
      detail?.pg_brand ?? detail?.paymentRequest?.gateway?.pg_brand;
    const isBalancePayment = detail?.balancePayment === "Y";

    if (!utr) {
      OpenNotification("danger", "Missing UPI transaction id.");
      return;
    }

    if (!billAmount) {
      OpenNotification("danger", "Missing bill amount.");
      return;
    }

    if (isBalancePayment) {
      const billOrderId =
        detail?.billOrderId ?? detail?.paymentRequest?.billOrderId;
      const salesExecutiveId =
        detail?.salesExecutiveId ?? detail?.paymentRequest?.salesExecutiveId;

      if (!billOrderId) {
        OpenNotification("danger", "Missing order reference for balance payment.");
        return;
      }

      if (!salesExecutiveId) {
        OpenNotification("danger", "Missing sales executive id.");
        return;
      }

      setProcessingUtr(utr);
      setSearchMessage("");

      try {
        const result = await processSqrBalancePayment({
          billOrderId,
          balancePaid: billAmount,
          salesExecutiveId,
          pgId: pgIdValue,
          pg_brand: pgBrandValue,
          utr,
        });

        if (result.success) {
          const callbacks = getSqrPaymentCallbacks();
          callbacks?.onSuccess?.({ status: "SUCCESS", utr });
          completeSqrPaymentFlow({ action: "process", status: "SUCCESS", utr });
          onClose?.();
          return;
        }

        setSearchMessage("Unable to process balance payment. Please try again.");
        OpenNotification("danger", "Unable to process balance payment.");
      } catch (error) {
        console.error(error);
        setSearchMessage("Unable to process balance payment. Please try again.");
        OpenNotification("danger", "Unable to process balance payment.");
      } finally {
        setProcessingUtr(null);
      }
      return;
    }

    const invoiceId =
      detail?.invoiceId ?? detail?.paymentRequest?.invoiceNumber;

    if (!invoiceId) {
      OpenNotification("danger", "Missing invoice reference for this bill.");
      return;
    }

    setProcessingUtr(utr);
    setSearchMessage("");

    try {
      const result = await processSqrUpiTransaction({
        invoiceId,
        utr,
        tenderAmt: billAmount,
        pgId: pgIdValue,
        pg_brand: pgBrandValue,
      });

      if (result.success) {
        const callbacks = getSqrPaymentCallbacks();
        callbacks?.onSuccess?.({ status: "SUCCESS", utr });
        completeSqrPaymentFlow({ action: "process", status: "SUCCESS", utr });
        onClose?.();
        return;
      }

      setSearchMessage("Unable to process payment. Please try again.");
      OpenNotification("danger", "Unable to process payment.");
    } catch (error) {
      console.error(error);
      setSearchMessage("Unable to process payment. Please try again.");
      OpenNotification("danger", "Unable to process payment.");
    } finally {
      setProcessingUtr(null);
    }
  };

  const handleCash = () => {
    const callbacks = getSqrPaymentCallbacks();
    markCashModalFromPaymentInitiated();
    callbacks?.onCash?.({ fromPaymentInitiated: true });
    completeSqrPaymentFlow({ action: "cash" });
    (onDismiss || onClose)?.();
  };

  const handleOnline = () => {
    const callbacks = getSqrPaymentCallbacks();
    callbacks?.onOnline?.();
    completeSqrPaymentFlow({ action: "online" });
    (onDismiss || onClose)?.();
  };

  const handleDialogClose = () => {
    completeSqrPaymentFlow({ action: "close" });
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <Dialog
      contentClassName="p-0 overflow-hidden z-[200] max-w-4xl w-full mx-4"
      closable={false}
      isOpen={isOpen}
      onClose={handleDialogClose}
    >
      <div className="px-4 py-3 bg-slate-50 border-t-4 flex items-center justify-between">
        <h5 className="font-semibold text-slate-800">UPI Transactions</h5>
        <button
          type="button"
          className="cursor-pointer text-slate-500 hover:text-slate-800"
          onClick={handleDialogClose}
          aria-label="Close"
        >
          <HiX size={20} />
        </button>
      </div>

      <div className="px-4 py-3 flex justify-end">
        <Button
          size="sm"
          variant="solid"
          loading={loading}
          icon={<IoReloadCircleSharp />}
          onClick={handleReload}
          style={RELOAD_BTN_STYLE}
        >
          Reload
        </Button>
      </div>

      <div className="px-4 pb-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Last 4 digits Only
            </label>
            <Input
              size="sm"
              placeholder="UPI Txn Id"
              value={utrQuery}
              onChange={(e) => setUtrQuery(e.target.value.replace(/\D/g, ""))}
              maxLength={4}
            />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Amount in Rs
            </label>
            <Input
              size="sm"
              placeholder="Amount"
              value={amountRs}
              onChange={(e) =>
                setAmountRs(e.target.value.replace(/[^\d.]/g, ""))
              }
            />
          </div>
          <Button
            size="sm"
            variant="solid"
            loading={loading}
            icon={<HiOutlineSearch />}
            onClick={handleSearch}
            style={LIGHT_GREY_BTN_STYLE}
          >
            Search
          </Button>
          <Button
            size="sm"
            variant="solid"
            loading={loading}
            onClick={handleReset}
            style={RESET_BTN_STYLE}
          >
            Reset
          </Button>
        </div>
        {searchMessage && (
          <p className="mt-2 text-sm text-amber-600">{searchMessage}</p>
        )}
      </div>

      <div className="px-4 pb-2 text-sm font-semibold text-slate-700">
        {recordCount} Record{recordCount === 1 ? "" : "s"} Found.
      </div>

      <div className="px-4 pb-3 max-h-[45vh] overflow-auto sqr-payment-table">
        <Table compact className="text-[10px]">
          <THead>
            <Tr>
              <Th className="text-[10px] py-2">Date</Th>
              <Th className="text-[10px] py-2">Amount</Th>
              <Th className="text-[10px] py-2">Name</Th>
              <Th className="text-[10px] py-2">UPI-TxnId</Th>
              <Th className="text-[10px] py-2">Action</Th>
            </Tr>
          </THead>
          <TBody>
            {displayedTransactions.length === 0 && (
              <Tr>
                <Td
                  colSpan={5}
                  className="text-center text-slate-500 py-6 text-[10px]"
                >
                  No transactions found.
                </Td>
              </Tr>
            )}
            {displayedTransactions.map((txn, index) => {
              const { date, time } = formatTransactionDateTime(
                txn?.transactionDate
              );

              return (
              <Tr key={`${txn?.utr || "txn"}-${index}`}>
                <Td className="text-[10px] leading-tight py-3">
                  <div>{date}</div>
                  {time && <div>{time}</div>}
                </Td>
                <Td className="text-[10px] py-3">
                  {txn?.amount != null ? Number(txn.amount) / 100 : "-"}
                </Td>
                <Td className="text-[10px] max-w-[140px] truncate py-3">
                  {txn?.name || "-"}
                </Td>
                <Td className="text-[10px] font-mono py-3">{txn?.utr || "-"}</Td>
                <Td className="py-3">
                  <Button
                    size="xs"
                    variant="solid"
                    className="text-[10px]"
                    loading={processingUtr === txn?.utr}
                    disabled={loading || Boolean(processingUtr)}
                    onClick={() => handleProcess(txn)}
                  >
                    Process
                  </Button>
                </Td>
              </Tr>
              );
            })}
          </TBody>
        </Table>
      </div>

      <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-start gap-3 bg-slate-50">
        <Button
          size="sm"
          variant="solid"
          onClick={handleCash}
          style={LIGHT_GREY_BTN_STYLE}
        >
          Pay Cash
        </Button>
        <Button
          size="sm"
          variant="solid"
          onClick={handleOnline}
          style={LIGHT_GREY_BTN_STYLE}
        >
          Pay Online
        </Button>
      </div>
    </Dialog>
  );
};

function normalizeStatus(status) {
  return String(status || "").trim().toUpperCase();
}

export default SqrPaymentTransactionsModal;
